import { Badge, Button, Card, Input } from '@bienenkampf/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { formatDateTime } from '../lib/time.js';
import { useTRPC } from '../lib/trpc.js';

export function LandingPage() {
  const trpc = useTRPC();
  const me = useQuery(trpc.auth.me.queryOptions());
  const worlds = useQuery(trpc.world.list.queryOptions());

  return (
    <div className="flex flex-col gap-6 py-4">
      {!me.data?.account && (
        <Card>
          <h1 className="text-2xl font-semibold">Führe dein Volk.</h1>
          <p className="mt-2 text-stone-300">
            Nektar und Pollen holst du dir auf umkämpften Blütenfeldern. Baue Waben, zieh Bienen
            heran, züchte Königinnen. Der Winter ist hart, aber er nimmt dir nie dein Volk.
          </p>
          <div className="mt-4 flex gap-2">
            <Link to="/register">
              <Button>Konto anlegen</Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost">Anmelden</Button>
            </Link>
          </div>
        </Card>
      )}

      <Card title="Welten">
        {worlds.isPending && <p className="text-stone-400">Lade…</p>}
        {worlds.isError && <p className="text-red-400">{worlds.error.message}</p>}
        <ul className="flex flex-col gap-3">
          {worlds.data?.map((w) => {
            const mine = me.data?.players.find((p) => p.worldId === w.id);
            return (
              <li
                key={w.id}
                className="flex flex-col gap-3 rounded-lg bg-stone-900 p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <div className="flex items-center gap-2 font-medium">
                    {w.name}
                    <Badge tone={w.season.season === 'WINTER' ? 'sky' : 'amber'}>
                      {w.season.season === 'WINTER' ? 'Winter' : 'Tracht'}
                    </Badge>
                  </div>
                  <div className="text-sm text-stone-400">
                    Speed {w.speed} · {w.playerCount} Spieler · Saisonwechsel{' '}
                    {formatDateTime(w.season.endsAt)} · Imkerschutz {w.protectionHours} h
                  </div>
                  {w.victoryCondition && (
                    <div className="mt-1 text-xs text-amber-200/80">🏆 {w.victoryCondition}</div>
                  )}
                </div>
                {mine ? (
                  <Link to="/w/$worldId" params={{ worldId: String(w.id) }}>
                    <Button>Als {mine.name} spielen</Button>
                  </Link>
                ) : me.data?.account ? (
                  <JoinForm worldId={w.id} />
                ) : (
                  <Link to="/register">
                    <Button variant="ghost">Beitreten</Button>
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </Card>
    </div>
  );
}

function JoinForm({ worldId }: { worldId: number }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const join = useMutation(
    trpc.world.join.mutationOptions({
      onSuccess: async () => {
        await qc.invalidateQueries({ queryKey: trpc.auth.me.queryKey() });
        await navigate({ to: '/w/$worldId', params: { worldId: String(worldId) } });
      },
    }),
  );
  return (
    <form
      className="flex items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        join.mutate({ worldId, playerName: name });
      }}
    >
      <Input
        name={`name-${worldId}`}
        label="Name deiner Königin"
        placeholder="z. B. Melissa"
        required
        minLength={3}
        maxLength={20}
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={join.error?.message}
      />
      <Button type="submit" loading={join.isPending}>
        Beitreten
      </Button>
    </form>
  );
}
